import { DOCUMENT } from '@angular/common';
import type { OnInit } from '@angular/core';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconRegistry } from '@angular/material/icon';
import { Title } from '@angular/platform-browser';
import { NavigationStart, Router, RouterOutlet } from '@angular/router';
import { Store } from '@ngrx/store';
import { NgProgressbar } from 'ngx-progressbar';
import { NgProgressHttp } from 'ngx-progressbar/http';
import { fromEvent } from 'rxjs';
import { combineLatestWith, filter, take, tap } from 'rxjs/operators';

import { TranslateService } from 'src/app/core/services/translate/translate.service';

import { ChatSidebarComponent } from './chat/components/chat-sidebar/chat-sidebar.component';
import { ChatSidebarService } from './chat/services/chat-sidebar.service';
import * as fromAuth from './auth/store';
import { DEFAULT_SEARCH_SORT_OPTION, DEFAULT_USER_LOCALE, LocalStorageItem } from './core/constants';
import { UtilsService } from './core/services/helpers/utils.service';
import { LayoutChangesService } from './core/services/layout-changes/layout-changes.service';
import { LocalStorageService } from './core/services/local-storage/local-storage.service';
import * as fromCore from './core/store';
import { getWindowHeight, getWindowWidth } from './core/store/layout/layout.actions';
import { asLocale } from './core/enums/locale.enum';
import { useLocale } from './core/store/translations/translations.actions';
import { LoadFailedComponent } from './shared/components/load-failed/load-failed.component';
import { debounced } from './shared/decorators/debounce.decorator';
import { getExternalSources } from './shared/modules/external-sharing/store/external-sharing.actions';
import { injectAfterRender } from './shared/utils/after-render.util';
import { selectCurrentUser } from './user/store';
import { policy } from './core/tier';
import { DialogService } from './shared/services/dialog/dialog.service';
import { CurrentUserService } from './user/services/current-user.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [RouterOutlet, NgProgressbar, NgProgressHttp, ChatSidebarComponent, LoadFailedComponent],
  styles: [
    `
      .chat-sidebar-overlay {
        position: fixed;
        inset: 0;
        z-index: 1002;
        background: rgba(0, 0, 0, 0.2);
      }
      .chat-sidebar-panel {
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        width: 420px;
        max-width: 100vw;
        z-index: 1003;
        box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
        animation: slide-in 0.2s ease-out;
      }
      @keyframes slide-in {
        from {
          transform: translateX(100%);
        }
        to {
          transform: translateX(0);
        }
      }
    `,
  ],
  host: {
    '(window:resize)': 'onResize($event)',
  },
})
export class AppComponent implements OnInit {
  private translate = inject(TranslateService);
  private utils = inject(UtilsService);
  private currentUser = inject(CurrentUserService);
  private router = inject(Router);
  private titleService = inject(Title);
  private document = inject<Document>(DOCUMENT);
  private layoutChangesService = inject(LayoutChangesService);
  private localStorageService = inject(LocalStorageService);
  private dialog = inject(DialogService);
  private matIconReg = inject(MatIconRegistry);
  private destroyRef = inject(DestroyRef);
  private afterRender = injectAfterRender();

  readonly chatSidebar = inject(ChatSidebarService);
  private readonly store = inject<Store>(Store);
  loggedIn$ = this.store.select(fromAuth.getLoggedIn);

  onResize = debounced((event: Event) => {
    const target = event.target;
    if (!(target instanceof Window)) {
      return;
    }
    this.store.dispatch(getWindowHeight({ height: target.innerHeight }));
    this.store.dispatch(getWindowWidth({ width: target.innerWidth }));
  }, 100);

  constructor() {
    this.localStorageService.init();
    const addedLocales = this.store.select(fromCore.getAvailableLocales);
    addedLocales.pipe(takeUntilDestroyed(this.destroyRef), take(1)).subscribe(availLocales => {
      const defLocale = DEFAULT_USER_LOCALE;
      const browserLocale = this.translate.getBrowserCultureLang().toLowerCase();
      const dashRemover = (l: string) => l.split('-')[0];
      const coreLocale = availLocales.map(l => dashRemover(l));
      const coreLocaleIndex = coreLocale.findIndex(l => l === browserLocale);
      const extraCultureLocale = dashRemover(browserLocale);
      const extraCultureLocaleIndex = coreLocale.findIndex(l => l === extraCultureLocale);
      const browserLocaleValue = asLocale(browserLocale);
      const currentLocale =
        browserLocaleValue !== undefined && availLocales.includes(browserLocaleValue)
          ? browserLocaleValue
          : coreLocale.includes(browserLocale)
            ? availLocales[coreLocaleIndex]
            : coreLocale.includes(extraCultureLocale)
              ? availLocales[extraCultureLocaleIndex]
              : defLocale;
      let usedLocale = currentLocale ?? defLocale;
      try {
        usedLocale = this.currentUser.user.locale;
      } catch {
        // boot before any login: the browser/default locale stands
      }
      this.document.documentElement.lang = usedLocale;
      this.store.dispatch(useLocale({ locale: usedLocale }));
    });
    this.matIconReg.setDefaultFontSetClass('material-symbols-outlined');
  }
  ngOnInit() {
    this.subscribeOnCurrentUser();
    this.checkNavigationPath();
    this.getExternalSources();
    if (policy.isMonitored) {
      void import('@sentry/angular').then(sentry => sentry.setTag('server', this.document.location.hostname));
    }
    this.setVersion();
    this.setLariTitle();
    this.preloadEditor();
  }
  private preloadEditor(): void {
    this.loggedIn$.pipe(takeUntilDestroyed(this.destroyRef), filter(Boolean), take(1)).subscribe(() => {
      this.afterRender(() => {
        // without the timeout an idle callback can starve indefinitely (#4804)
        requestIdleCallback(
          () => void import('./shared/modules/hugerte/components/hugerte-editor/hugerte-editor.component').catch(() => {}),
          { timeout: 3000 },
        );
      });
    });
  }
  checkNavigationPath(): void {
    fromEvent<StorageEvent>(window, 'storage')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        const { url } = this.router;
        const returnURL = ['/login', '/lariuser'].some(r => url.includes(r)) ? '' : window.location.pathname + window.location.search;
        if (event.key === LocalStorageItem.AccessToken && event.newValue === null) {
          void this.utils.navigation.goToUnAuthPage(returnURL);
        }
      });
  }
  setLariTitle(): void {
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(e => {
          if (e instanceof NavigationStart) {
            // a list page navigating to normalise its own query params has already set its title,
            // so only a different path is a new page
            if (e.url.split('?')[0] !== this.router.url.split('?')[0]) {
              this.titleService.setTitle('LARI');
            }
            this.dialog.closeAll();
          }
        }),
      )
      .subscribe();
  }
  getExternalSources(): void {
    this.loggedIn$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isLoggedIn => {
      if (isLoggedIn) {
        this.store.dispatch(getExternalSources());
      }
    });
  }
  subscribeOnCurrentUser(): void {
    this.store
      .select(selectCurrentUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        if (user) {
          this.checkScreenWidth();
        }
      });
  }
  checkScreenWidth(): void {
    this.layoutChangesService.windowWidth$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        combineLatestWith(
          this.layoutChangesService.isMobileSecondarySidebarVisible$,
          this.layoutChangesService.isMobileMainSidebarVisible$,
        ),
        tap(([width, isMobileSecondarySidebarVisible, isMobileMainSidebarVisible]) => {
          const sidebarState = this.utils.storage.get('sidebarState');
          const isLessThanMedium = width < 960;
          if (sidebarState !== null) {
            if (+sidebarState) {
              /**
               * Always keep sidebar collapsed if it was collapsed manually
               * */
              this.layoutChangesService.collapseMainSidebar(true);
            } else {
              this.layoutChangesService.collapseMainSidebar(isLessThanMedium);
            }
          } else {
            this.layoutChangesService.collapseMainSidebar(isLessThanMedium);
          }
          if (width > 599) {
            if (isMobileSecondarySidebarVisible) {
              this.layoutChangesService.toggleMobileSecondarySidebar(false);
            }
            if (isMobileMainSidebarVisible) {
              this.layoutChangesService.toggleMobileMainSidebar(false);
            }
          }
        }),
      )
      .subscribe();
  }

  setVersion() {
    const version = this.utils.storage.get('appVersion') ?? 0;
    if (version < 1) {
      this.utils.storage.set('webSearchSorting', DEFAULT_SEARCH_SORT_OPTION);
      this.utils.storage.set('appVersion', 1);
      return;
    }
  }
}
